<template>
  <div
    class="favorite-card relative"
    tabindex="-1"
    role="group"
    :aria-label="favorite.cardProps.name"
    :data-favorite-card="favorite.cardProps.sitterIdForProfile"
  >
    <RouterLink
      v-if="profileUrl"
      :to="profileUrl"
      class="favorite-card-profile-link"
      :aria-label="`View ${favorite.cardProps.name}'s profile`"
    />
    <!-- The sibling profile link covers the card without nesting its action controls. -->
    <SitterCard v-bind="favorite.cardProps" :sitter-id-for-profile="null" custom-classes="w-full">
      <template #actions>
        <Button
          v-if="favorite.hasConversation"
          :to="{ path: '/messages', query: { uuid: favorite.cardProps.sitterIdForProfile } }"
          value="See Messages"
          variant="text"
          size="medium"
          class="favorite-card-action whitespace-nowrap"
          :aria-label="`See Messages with ${favorite.cardProps.name}`"
        />
        <Button
          type="button"
          value="Remove"
          variant="text"
          size="medium"
          class="favorite-card-action whitespace-nowrap"
          :aria-label="`Remove ${favorite.cardProps.name} from favorites`"
          :data-remove-favorite="favorite.cardProps.sitterIdForProfile"
          :disabled="removing"
          @click="emit('remove', favorite.cardProps.sitterIdForProfile, favorite.cardProps.name, $event)"
        />
      </template>
    </SitterCard>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import Button from '@/components/button/Button.vue';
import SitterCard from '@/components/sitter_card/SitterCard.vue';
import getProviderProfileUrl from '@/helpers/providerProfileUrl';
import type { FavoriteCard } from './types';

const props = defineProps<{ favorite: FavoriteCard; removing: boolean }>();
const emit = defineEmits<{ remove: [sitterId: string, name: string, event: MouseEvent] }>();

const profileUrl = computed(() =>
  getProviderProfileUrl(props.favorite.cardProps.sitterIdForProfile, props.favorite.cardProps.caretype)
);
</script>

<style scoped lang="scss">
.favorite-card-profile-link {
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: 4px;
}

.favorite-card-action {
  position: relative;
  z-index: 2;
}

.favorite-card:has(.favorite-card-profile-link):hover :deep(.card) {
  box-shadow: theme('boxShadow.md');
}

.favorite-card:focus-visible,
.favorite-card-profile-link:focus-visible {
  outline: 2px dotted theme('colors.brand.contrast');
  outline-offset: 4px;
}
</style>
